/**
 * The Ornament menu: the signs that ask a player for notes and time nobody wrote down.
 *
 * Its own file rather than another section of the Note menu, because the vocabulary is long
 * -- eight ornaments, three fermata shapes, three arpeggios and three tremolo counts -- and
 * a Note menu carrying all of it would be a scroll rather than a menu.
 *
 * A tick here means every selected note carries the mark, not merely one of them, which is
 * the same rule the editing layer toggles by: a mixed selection is shown unticked and the
 * first click makes it uniform.
 */

import { marks, selection as select } from "@app/score-editor";
import type { Arpeggio, FermataShape, Ornament, Score, Selection } from "@app/score-editor";
import type { ScoreActions } from "./score-actions";
import { RULE } from "./score-menu-model";
import type { Menu, MenuEntry } from "./score-menu-model";

/** Every ornament, in the order a musician would look for it. */
const ORNAMENTS: readonly { mark: Ornament; label: string }[] = [
  { mark: "trill", label: "Trill" },
  { mark: "mordent", label: "Mordent" },
  { mark: "inverted-mordent", label: "Inverted mordent" },
  { mark: "turn", label: "Turn" },
  { mark: "inverted-turn", label: "Inverted turn" },
  { mark: "delayed-turn", label: "Delayed turn" },
  { mark: "shake", label: "Shake" },
  { mark: "schleifer", label: "Schleifer" },
];

const FERMATAS: readonly { shape: FermataShape; label: string }[] = [
  { shape: "normal", label: "Fermata" },
  { shape: "angled", label: "Fermata, angled" },
  { shape: "square", label: "Fermata, square" },
];

const ARPEGGIOS: readonly { style: Arpeggio; label: string }[] = [
  { style: "up", label: "Arpeggio, upwards" },
  { style: "down", label: "Arpeggio, downwards" },
  { style: "bracket", label: "Bracket, not spread" },
];

const TREMOLOS: readonly { beams: number; label: string }[] = [
  { beams: 1, label: "Tremolo, one beam" },
  { beams: 2, label: "Tremolo, two beams" },
  { beams: 3, label: "Tremolo, three beams" },
];

/** Whether every selected note satisfies a test, with an empty selection counting as no. */
function everyNote(
  score: Score,
  selection: Selection,
  holds: (event: { readonly ornaments: readonly Ornament[] }) => boolean,
): boolean {
  const found = select.selectedNotes(score, selection);
  return found.length > 0 && found.every((at) => holds(at.event));
}

export function ornamentMenu(actions: ScoreActions): Menu {
  const { score, selection } = actions;
  const nothing = selection.events.size === 0;

  const notes = select.selectedNotes(score, selection);
  const chords = notes.filter((at) => at.event.pitches.length > 1);
  const held = select.selectedLocations(score, selection);

  const ornaments: MenuEntry[] = ORNAMENTS.map((entry) => ({
    label: entry.label,
    disabled: nothing,
    checked: everyNote(score, selection, (event) => event.ornaments.includes(entry.mark)),
    run: () => actions.edit(marks.ornament(score, selection, entry.mark)),
  }));

  const fermatas: MenuEntry[] = FERMATAS.map((entry) => ({
    label: entry.label,
    disabled: nothing,
    checked: held.length > 0 && held.every((at) => at.event.fermata === entry.shape),
    run: () => actions.edit(marks.fermata(score, selection, entry.shape)),
  }));

  const arpeggios: MenuEntry[] = ARPEGGIOS.map((entry) => ({
    label: entry.label,
    disabled: chords.length === 0,
    checked: chords.length > 0 && chords.every((at) => at.event.arpeggio === entry.style),
    run: () => actions.edit(marks.arpeggio(score, selection, entry.style)),
  }));

  const tremolos: MenuEntry[] = TREMOLOS.map((entry) => ({
    label: entry.label,
    disabled: nothing,
    checked: notes.length > 0 && notes.every((at) => at.event.tremolo === entry.beams),
    run: () => actions.edit(marks.tremolo(score, selection, entry.beams)),
  }));

  return {
    title: "Ornament",
    items: [
      ...ornaments,
      {
        label: "No ornament",
        disabled: nothing,
        run: () => actions.edit(marks.clearOrnaments(score, selection)),
      },
      RULE,
      ...fermatas,
      {
        label: "No fermata",
        disabled: nothing,
        run: () => actions.edit(marks.fermata(score, selection, null)),
      },
      RULE,
      ...arpeggios,
      {
        label: "No arpeggio",
        disabled: chords.length === 0,
        run: () => actions.edit(marks.arpeggio(score, selection, null)),
      },
      RULE,
      ...tremolos,
      {
        label: "No tremolo",
        disabled: nothing,
        run: () => actions.edit(marks.tremolo(score, selection, 0)),
      },
    ],
  };
}
